import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { CartContext } from '../contexts/CartContext';
import logo from '../assets/images/logo.png';
import './Navbar.css';

export default function Navbar() {
  const { user, logout, ordersBadge } = useContext(AuthContext);
  const { getItemCount } = useContext(CartContext);
  const navigate  = useNavigate();
  const location  = useLocation();

  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
      navigate('/login');
    }
  };

  const getNavLinks = () => {
    const role = user?.role?.toLowerCase();

    if (role === 'admin') {
      return [
        { path: '/admin',              label: '📊 Dashboard' },
        { path: '/admin/products',     label: '🥬 Products'  },
        { path: '/admin/orders',       label: '📦 Orders'    },
        { path: '/admin/riders',       label: '🏍️ Riders'    },
        { path: '/admin/admins',       label: '👑 Admins'    },   // ✅ NEW
        { path: '/admin/rider-earnings', label: '💰 Earnings' }
      ];
    }

    if (role === 'rider') {
      return [
        { path: '/rider',           label: '🏠 Dashboard' },
        { path: '/rider/orders',    label: '📦 Orders'    },
        { path: '/rider/delivered', label: '✅ Delivered' },
        { path: '/rider/profile',   label: '👤 Profile'   }
      ];
    }

    return [
      { path: '/user',         label: '🏠 Home'                          },
      { path: '/user/cart',    label: '🛒 Cart',   badge: getItemCount() },
      { path: '/user/orders',  label: '📦 Orders', badge: ordersBadge    },
      { path: '/user/profile', label: '👤 Profile'                       }
    ];
  };

  const navLinks = getNavLinks();

  return (
    <>
      <nav className="navbar">

        {/* Logo */}
        <Link to="/" className="navbar-logo">
          <img
            src={logo}
            alt="Ghar Tak Fresh Logo"
            className="logo-img"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'inline';
            }}
          />
          <span className="logo-emoji-fallback" style={{ display: 'none' }}>🥬</span>
          <span className="logo-text">Ghar Tak Fresh</span>
        </Link>

        {/* Desktop Nav Links */}
        <div className="navbar-links">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
            >
              {link.label}
              {link.badge > 0 && <span className="nav-badge">{link.badge}</span>}
            </Link>
          ))}
        </div>

        {/* Desktop User Section */}
        <div className="navbar-user">
          <div className="user-info">
            <div className="user-avatar">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="user-details">
              <span className="user-name">{user?.name || 'User'}</span>
              <span className="user-role">{user?.role?.toUpperCase()}</span>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            🚪 Logout
          </button>
        </div>

        {/* Hamburger */}
        <button
          className={`hamburger-btn ${drawerOpen ? 'open' : ''}`}
          onClick={() => setDrawerOpen(!drawerOpen)}
          aria-label="Toggle menu"
        >
          <span></span><span></span><span></span>
        </button>

      </nav>

      {/* Mobile Drawer */}
      {drawerOpen && (
        <div className="mobile-drawer open">
          <div className="drawer-backdrop" onClick={() => setDrawerOpen(false)} />
          <div className="drawer-panel">
            <div className="drawer-user-block">
              <div className="drawer-avatar">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div>
                <div className="drawer-user-name">{user?.name || 'User'}</div>
                <span className="drawer-user-role">{user?.role?.toUpperCase()}</span>
              </div>
            </div>

            <div className="drawer-section-label">Navigation</div>

            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`drawer-link ${location.pathname === link.path ? 'active' : ''}`}
                onClick={() => setDrawerOpen(false)}
              >
                <span className="d-label">{link.label}</span>
                {link.badge > 0 && <span className="d-badge">{link.badge}</span>}
                <span className="d-arrow">›</span>
              </Link>
            ))}

            <div className="drawer-sep" />
            <button className="drawer-logout" onClick={handleLogout}>
              🚪 Logout
            </button>
          </div>
        </div>
      )}
    </>
  );
}